import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const counterVariants = cva(
  "pointer-events-none inline-flex items-center justify-center rounded-xs font-inter text-xs font-semibold leading-tight-sm tabular-nums data-[loading=true]:opacity-50",
  {
    variants: {
      variant: {
        default: "min-w-7 p-1 bg-dinnerplate-white text-grey",
        soft: "px-1.5 py-0.5 bg-dinnerplate-white-light text-grey",
        destructive: "min-w-7 px-2 py-1 bg-chilli-blush font-normal text-chilli-red",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
)

export interface CounterProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof counterVariants> {
  ref?: React.Ref<HTMLSpanElement>
  value: number | string
}

/**
 * Small pill counter shown next to a label (e.g. EVENTS 2, Results 2).
 *
 * - `default` — dinnerplate-white pill, min 21px square, grows for multi-digit counts.
 * - `soft` — lighter dinnerplate-white-light pill, used in PopOutList headers.
 * - `destructive` — chilli-blush pill with chilli-red text, for counts that need attention.
 *
 * Pass `data-loading` to dim the counter while its value is still loading.
 */
function Counter({ className, value, variant, ref, ...props }: CounterProps) {
  return (
    <span
      ref={ref}
      className={cn(counterVariants({ variant }), className)}
      {...props}
    >
      {value}
    </span>
  )
}

export { Counter, counterVariants }
